import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Session } from '@/domain/schema/v1';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { flushAll } from '../../state/saves';
import { RangeCard } from '../../components/RangeCard';

const mmss = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

export type RangeDocKind = 'dialogue' | 'verbatim';
const KIND_NAME: Record<RangeDocKind, string> = { dialogue: '대화록', verbatim: '축어록' };

/**
 * 추천 구간 중 하나를 고르면 바로 대화록(수업용) 또는 축어록을 만든다.
 * 추천은 AI 분석의 변화 지점 구간이 먼저, 모자라면 앱 후보. 구간을 미리 저장할 필요 없음.
 * 원하는 구간을 직접 지정하려면 전사 화면의 구간 지정으로 바로 간다.
 */
export function RangeDocMaker({ session, kind }: { session: Session; kind: RangeDocKind }) {
  const svc = useService();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { data } = useLoad(
    async () => ({
      candidates: await svc.dialogueCandidates(session.id),
      docs: (await svc.listDocuments(session.clientId)).filter(
        (d) => d.documentType === kind && d.sessionId === session.id && !d.deletedAt,
      ),
      saved:
        kind === 'verbatim'
          ? (await svc.listVerbatimSelections(session.clientId)).filter(
              (v) => v.sessionId === session.id && v.status === 'selected' && !v.deletedAt,
            )
          : [],
    }),
    [svc, session.id, kind],
  );
  const open = (id: string) => navigate(`/clients/${session.clientId}/documents?doc=${id}`);
  const make = (
    key: string,
    r: { startMs: number; endMs: number; reason?: string; supervisionQuestions?: string[] },
  ) => {
    setBusy(key);
    setErr(null);
    void flushAll()
      .then(() =>
        kind === 'dialogue' ? svc.createDialogueFromRange(session.id, r) : svc.createVerbatimFromRange(session.id, r),
      )
      .then((d) => open(d.id))
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(null));
  };
  if (!data) return <p className="muted small">추천 구간 찾는 중…</p>;
  return (
    <div className="stack">
      {data.docs.length > 0 && (
        <p className="small">
          이 회기 {KIND_NAME[kind]} {data.docs.length}개 —{' '}
          {data.docs.map((d, k) => (
            <button key={d.id} type="button" className="link" onClick={() => open(d.id)}>
              {k + 1}번 열기{' '}
            </button>
          ))}
        </p>
      )}
      {data.candidates.length === 0 && (
        <p className="muted small">
          추천할 구간이 없습니다 (전사가 짧거나 아직 처리되지 않았습니다). 아래에서 직접 지정하세요.
        </p>
      )}
      {data.candidates.map((c) => (
        <RangeCard
          key={c.key}
          title={c.source === 'ai' ? 'AI 변화 지점' : '추천 구간'}
          startMs={c.startMs}
          endMs={c.endMs}
          sessionEndMs={c.sessionEndMs}
          points={c.points}
          reason={c.reason}
          lines={c.lines}
          actions={
            <button
              type="button"
              className="primary"
              disabled={!!busy}
              onClick={() =>
                make(c.key, {
                  startMs: c.startMs,
                  endMs: c.endMs,
                  ...(c.reason ? { reason: c.reason } : {}),
                  ...(c.supervisionQuestions ? { supervisionQuestions: c.supervisionQuestions } : {}),
                })
              }
            >
              {busy === c.key ? '만드는 중…' : `이 구간으로 ${KIND_NAME[kind]} 만들기`}
            </button>
          }
        />
      ))}
      {data.saved.length > 0 && (
        <div className="stack small">
          <strong>저장해 둔 구간</strong>
          {data.saved.map((v) => (
            <div key={v.id} className="row">
              <span>
                {mmss(v.startMs)} ~ {mmss(v.endMs)}
                {v.selectionReason && <span className="muted"> — {v.selectionReason}</span>}
              </span>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => void svc.createVerbatimDocument(v.id).then((d) => open(d.id))}
              >
                이 구간으로 축어록 만들기
              </button>
            </div>
          ))}
        </div>
      )}
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
      <Link className="small" to={`/sessions/${session.id}?ranges=1`}>
        원하는 구간을 직접 지정하기 (전사 화면) →
      </Link>
    </div>
  );
}

/** 회기 화면: 대화록 만들기 (수업용) */
export function DialogueMaker({ session }: { session: Session }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="card" onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>
        <strong>대화록 만들기 (수업용)</strong>{' '}
        <span className="muted small">추천 구간 중 하나를 고르면 대화록 작성표 양식으로 만듭니다</span>
      </summary>
      {open && <RangeDocMaker session={session} kind="dialogue" />}
      {open && (
        <p className="muted small">
          만든 뒤 문서 화면의 'AI로 채우기·고치기'에서 ① 외부로 보낼 자료 복사 → 외부 AI에 붙여넣기 → ② 외부 답
          붙여넣기로 I~VI를 채웁니다. 이 대화록은 사례보고서 등 다른 문서와 따로 관리됩니다.
        </p>
      )}
    </details>
  );
}
